import { FC, useEffect, useState } from "react";
import { Button, Spin, Typography } from "antd";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { getPageDetailApi } from "../../api/paymentPage";
import TransactionService from "../../services/transaction-service";
import {
  isTransactionSuccessful,
  isTransactionFailed,
  isTransactionPending,
} from "../../services/transaction-status";
import {
  IPaymentPageDetail,
  IPaymentQueryResponse,
  IPaymentCompletionConfig,
  IPaymentPageCompletionStorage,
} from "@app-models";
import MonnifyIcon from "../../common/icons";
import MerchantLeftPanel from "./components/MerchantLeftPanel";
import PaymentPageFooter from "./components/PaymentPageFooter";
import { pagePaymentCompletionStorage } from "../../store/store";
import {
  PAYMENT_STATUS,
  PaymentStatus,
  REDIRECT_TYPE,
  COUNTDOWN_SECONDS,
  MAX_AUTO_RETRIES,
} from "./constants";
import "./PaymentPageView.scss";

interface PaymentStatusContentProps {
  paymentStatus: PaymentStatus | null;
  countdown: number;
  retryCount: number;
  displayMessage: string | null;
  merchantName?: string;
}

const getIcon = (paymentStatus: PaymentStatus | null) => {
  switch (paymentStatus) {
    case PAYMENT_STATUS.SUCCESS:
      return <MonnifyIcon type="NEW_SUCCESS_ICON_2" />;
    case PAYMENT_STATUS.PENDING:
      return <Spin size="large" />;
    default:
      return <MonnifyIcon type="INFO_ICON" />;
  }
};

const getTitle = (paymentStatus: PaymentStatus | null) => {
  switch (paymentStatus) {
    case PAYMENT_STATUS.SUCCESS:
      return "Success!";
    case PAYMENT_STATUS.PENDING:
      return "Processing your payment";
    default:
      return "Payment was not successful";
  }
};

const getMessage = (
  paymentStatus: PaymentStatus | null,
  countdown: number,
  retryCount: number,
  displayMessage: string | null,
  merchantName?: string
) => {
  if (paymentStatus === PAYMENT_STATUS.PENDING) {
    if (retryCount < MAX_AUTO_RETRIES) {
      return (
        <>
          Your payment is being processed. We will recheck the status in{" "}
          <strong>{countdown}</strong> seconds...
        </>
      );
    }
    return "Your payment is taking longer than expected. Please click the button below to check the status manually.";
  }

  if (displayMessage) return displayMessage;

  switch (paymentStatus) {
    case PAYMENT_STATUS.SUCCESS:
      return `Your payment is on its way to ${merchantName}.`;
    default:
      return "Your payment could not be processed. Please try again.";
  }
};

const PaymentStatusContent: FC<PaymentStatusContentProps> = ({
  paymentStatus,
  countdown,
  retryCount,
  displayMessage,
  merchantName,
}) => {
  const icon = getIcon(paymentStatus);
  const title = getTitle(paymentStatus);
  const message = getMessage(paymentStatus, countdown, retryCount, displayMessage, merchantName);

  return (
    <>
      <div className="success-icon-wrap">{icon}</div>
      <Typography.Title level={2} className="success-title">
        {title}
      </Typography.Title>
      <Typography.Text className="success-subtitle">{message}</Typography.Text>
    </>
  );
};

const PaymentCompletion = () => {
  const { code } = useParams<{ code: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const pagePaymentCompletion = useRecoilValue(pagePaymentCompletionStorage);

  const [isLoading, setLoading] = useState(true);
  const [pageDetail, setPageDetail] = useState<IPaymentPageDetail | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | null>(null);
  const [displayMessage, setDisplayMessage] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);

  const verifyPayment = async (pagePaymentCompletion: IPaymentPageCompletionStorage | null) => {
    const paymentReference = searchParams.get("paymentReference");

    if (!paymentReference || !code) {
      navigate("/page/not-found");
      return;
    }

    if (!pagePaymentCompletion) {
      navigate("/page/not-found");
      return;
    }

    if (!pagePaymentCompletion.apiKey) {
      setPaymentStatus(PAYMENT_STATUS.FAILURE);
      setLoading(false);
      return;
    }

    try {
      const pageResponse = await getPageDetailApi(code);
      const pageData = pageResponse.data as IPaymentPageDetail;
      setPageDetail(pageData);

      const transactionResponse = await TransactionService.queryTransactionStatus(
        paymentReference,
        pagePaymentCompletion.apiKey,
        pageData.config.paymentEngineServiceUrl
      );

      const queryResponse = transactionResponse.data.responseBody as IPaymentQueryResponse;

      let status: PaymentStatus;
      if (isTransactionSuccessful(queryResponse.paymentStatus)) {
        status = PAYMENT_STATUS.SUCCESS;
      } else if (isTransactionFailed(queryResponse.paymentStatus)) {
        status = PAYMENT_STATUS.FAILURE;
      } else if (isTransactionPending(queryResponse.paymentStatus)) {
        status = PAYMENT_STATUS.PENDING;
      } else {
        status = PAYMENT_STATUS.FAILURE;
      }

      setPaymentStatus(status);

      if (status === PAYMENT_STATUS.PENDING && retryCount < MAX_AUTO_RETRIES) {
        setCountdown(COUNTDOWN_SECONDS);
        setLoading(false);
        return;
      }

      if (status === PAYMENT_STATUS.SUCCESS || status === PAYMENT_STATUS.FAILURE) {
        const matchingConfig = pagePaymentCompletion.paymentCompletionConfigs?.find(
          (config: IPaymentCompletionConfig) => config.configType === status
        );

        if (matchingConfig) {
          if (matchingConfig.redirectType === REDIRECT_TYPE.REDIRECT_URL) {
            (window.top || window).location.href = matchingConfig.value;
            return;
          } else if (matchingConfig.redirectType === REDIRECT_TYPE.MESSAGE) {
            setDisplayMessage(matchingConfig.value);
          }
        } else if (pageData.redirectUrl?.trim()) {
          (window.top || window).location.href = pageData.redirectUrl;
          return;
        }
      }

      setLoading(false);
    } catch (error) {
      console.error("Error verifying payment:", error);
      setPaymentStatus(PAYMENT_STATUS.FAILURE);
      setLoading(false);
    }
  };

  useEffect(() => {
    verifyPayment(pagePaymentCompletion);
  }, []);

  useEffect(() => {
    if (paymentStatus === PAYMENT_STATUS.PENDING && retryCount < MAX_AUTO_RETRIES) {
      if (countdown > 0) {
        const timer = setTimeout(() => {
          setCountdown((prev) => prev - 1);
        }, 1000);
        return () => clearTimeout(timer);
      }

      if (countdown === 0) {
        setRetryCount((prev) => prev + 1);
        setCountdown(COUNTDOWN_SECONDS);
      }
    }
  }, [paymentStatus, retryCount, countdown]);

  if (isLoading) {
    return (
      <div className="payment-page-container">
        <div className="payment-page-split">
          <div className="left-panel">
            <MerchantLeftPanel pageDetail={pageDetail} />
          </div>
          <div className="right-panel">
            <div className="success-right-content">
              <Spin size="large" />
              <Typography.Text style={{ display: "block", marginTop: 20, color: "#4d4d4d" }}>
                Verifying your payment...
              </Typography.Text>
            </div>
            <PaymentPageFooter />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-page-container">
      <div className="payment-page-split">
        <div className="left-panel">
          <MerchantLeftPanel pageDetail={pageDetail} />
        </div>
        <div className="right-panel">
          <div className="success-right-content">
            <PaymentStatusContent
              paymentStatus={paymentStatus}
              countdown={countdown}
              retryCount={retryCount}
              displayMessage={displayMessage}
              merchantName={pageDetail?.merchant?.name}
            />

            {(paymentStatus !== PAYMENT_STATUS.PENDING || retryCount >= MAX_AUTO_RETRIES) && (
              <Button
                onClick={() => {
                  navigate(`/${pageDetail?.code}`);
                }}
                type="primary"
                className="success-pay-again-btn"
              >
                {paymentStatus === PAYMENT_STATUS.PENDING && retryCount >= MAX_AUTO_RETRIES
                  ? "Back to payment form"
                  : paymentStatus === PAYMENT_STATUS.SUCCESS
                    ? "Pay Again"
                    : "Try Again"}
              </Button>
            )}

            <div className="success-support-text">
              Questions? Reach out to{" "}
              <a href={`mailto:${pageDetail?.merchant?.supportEmail}`}>
                {pageDetail?.merchant?.supportEmail}
              </a>
            </div>
          </div>
          <PaymentPageFooter />
        </div>
      </div>
    </div>
  );
};

export default PaymentCompletion;